import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  FileText,
  Image,
  Layers,
  Loader2,
  Music,
  Search,
  StickyNote,
  Video,
  X,
} from "lucide-react";

const FILTERS = [
  { value: "all", label: "全部" },
  { value: "image", label: "图片" },
  { value: "video", label: "视频" },
  { value: "text", label: "文本" },
  { value: "other", label: "其他" },
];

const TYPE_META = {
  image: { Icon: Image, label: "图片", color: "var(--color-blue-primary)" },
  video: { Icon: Video, label: "视频", color: "var(--color-green-primary)" },
  text: { Icon: FileText, label: "文本", color: "var(--color-warning)" },
  audio: { Icon: Music, label: "音频", color: "var(--color-pink-primary)" },
  other: { Icon: Layers, label: "其他", color: "var(--color-text-muted)" },
};

const LEGACY_LABELS = {
  aiDirector: "AI 导演",
  groupFrame: "分组",
  motionStoryboard: "影视转绘",
  scriptToStoryboard: "剧本转分镜",
  seedanceMerge: "视频合成",
  stickyNote: "便签",
  storyboardEpisode: "分镜集",
  storyboardGrid: "分镜宫格",
  storyboardShot: "分镜",
};

const getLegacyType = (node) => (
  node?.data?.legacyType
  || node?.data?.nodeKind
  || node?.type
  || ""
);

const inferNodeKind = (node) => {
  const data = node?.data || {};
  const normalizedKind = String(data.normalized?.kind || "").toLowerCase();
  if (["image", "video", "text", "audio"].includes(normalizedKind)) return normalizedKind;

  const resourceType = String(data.resourceType || "").toLowerCase();
  if (["image", "video", "text", "audio"].includes(resourceType)) return resourceType;

  const generateType = String(data.generateType || "").toLowerCase();
  if (generateType === "image" || generateType === "video") return generateType;

  const legacyType = getLegacyType(node);
  if (["storyboardGrid", "storyboardShot", "storyboardEpisode"].includes(legacyType)) return "image";
  if (["seedanceMerge", "motionStoryboard"].includes(legacyType)) return "video";
  if (["aiDirector", "scriptToStoryboard"].includes(legacyType)) return "text";
  return "other";
};

const getFilterKind = (kind) => (["image", "video", "text"].includes(kind) ? kind : "other");

const getNodeTypeLabel = (node, kind) => {
  const legacyType = getLegacyType(node);
  if (legacyType === "generate") {
    return kind === "video" ? "生成视频" : "生成图片";
  }
  if (LEGACY_LABELS[legacyType]) return LEGACY_LABELS[legacyType];
  return (TYPE_META[kind] || TYPE_META.other).label;
};

const stringValue = (value) => (typeof value === "string" ? value.trim() : "");

const getNodeTitle = (node, kind, typeLabel) => {
  const data = node?.data || {};
  const namedTitle = [
    data.name,
    data.normalized?.name,
    data.title,
    data.label,
  ].map(stringValue).find(Boolean);
  if (namedTitle) return namedTitle;

  if (getLegacyType(node) === "generate") {
    return kind === "video" ? "生成视频" : "生成图片";
  }
  return typeLabel || "未命名节点";
};

const isRenderableUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) return false;
  return /^(https?:|blob:|data:image\/|\/)/i.test(value.trim());
};

const getPrimaryMediaUrl = (data) => [
  data.normalized?.content?.url,
  data.url,
  data.lastResultUrl,
  data.result,
  data.generatedImage,
  data.generatedVideo,
  data.gridImageUrl,
].find(isRenderableUrl) || null;

const getNodeThumbnail = (node, kind) => {
  const data = node?.data || {};
  const primaryUrl = getPrimaryMediaUrl(data);

  if (kind === "image") {
    return [
      data.normalized?.content?.thumbnail,
      data.thumbnail,
      primaryUrl,
    ].find(isRenderableUrl) || null;
  }

  if (kind === "video") {
    // normalized.thumbnail 在没有独立封面时会回退为视频地址，列表中不能用它加载媒体。
    const explicitThumbnail = [data.thumbnail, data.poster, data.coverUrl]
      .find((value) => isRenderableUrl(value) && value !== primaryUrl);
    if (explicitThumbnail) return explicitThumbnail;

    const normalizedThumbnail = data.normalized?.content?.thumbnail;
    if (isRenderableUrl(normalizedThumbnail) && normalizedThumbnail !== primaryUrl) {
      return normalizedThumbnail;
    }
  }

  return null;
};

const getNodeStatus = (node) => {
  const data = node?.data || {};
  const statusValues = [
    data.status,
    data.taskStatus,
    data.generationStatus,
    data.generatingStatus,
    data.normalized?.status,
  ].map((value) => String(value || "").toLowerCase());

  const failed = data.isFailed
    || data.generationError
    || statusValues.some((value) => ["failed", "failure", "error", "cancelled", "canceled"].includes(value));
  if (failed) return { key: "failed", label: "失败" };

  if (data.uploading) return { key: "generating", label: "上传中" };
  const generating = data.isGenerating
    || data.generating
    || statusValues.some((value) => ["generating", "pending", "processing", "running", "queued", "submitted"].includes(value));
  if (generating) return { key: "generating", label: "生成中" };
  return null;
};

const getNodeIcon = (node, kind) => {
  const legacyType = getLegacyType(node);
  if (legacyType === "stickyNote") return StickyNote;
  return (TYPE_META[kind] || TYPE_META.other).Icon;
};

const getAbsolutePosition = (node, nodeMap, visited = new Set()) => {
  const knownAbsolute = node?.positionAbsolute || node?.computedPosition;
  if (knownAbsolute && Number.isFinite(knownAbsolute.x) && Number.isFinite(knownAbsolute.y)) {
    return knownAbsolute;
  }

  const own = {
    x: Number(node?.position?.x) || 0,
    y: Number(node?.position?.y) || 0,
  };
  if (!node?.parentId || visited.has(node.id)) return own;

  const parent = nodeMap.get(String(node.parentId));
  if (!parent) return own;
  const nextVisited = new Set(visited);
  nextVisited.add(node.id);
  const parentPosition = getAbsolutePosition(parent, nodeMap, nextVisited);
  return { x: own.x + parentPosition.x, y: own.y + parentPosition.y };
};

const stopCanvasEvent = (event) => event.stopPropagation();

export default function CanvasNodeListPanel({
  isOpen,
  onClose,
  nodes = [],
  selectedNodeIds = [],
  onLocateNode,
  onOpenNode,
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const frame = window.requestAnimationFrame(() => searchRef.current?.focus());
      return () => window.cancelAnimationFrame(frame);
    }

    if (panelRef.current?.contains(document.activeElement)) {
      document.activeElement?.blur();
    }
    return undefined;
  }, [isOpen]);

  const selectedSet = useMemo(() => {
    const values = selectedNodeIds instanceof Set
      ? Array.from(selectedNodeIds)
      : (Array.isArray(selectedNodeIds) ? selectedNodeIds : []);
    return new Set(values.map(String));
  }, [selectedNodeIds]);

  const preparedNodes = useMemo(() => {
    const safeNodes = Array.isArray(nodes) ? nodes.filter(Boolean) : [];
    const nodeMap = new Map(safeNodes.map((node) => [String(node.id), node]));

    return safeNodes.map((node) => {
      const kind = inferNodeKind(node);
      const typeLabel = getNodeTypeLabel(node, kind);
      const title = getNodeTitle(node, kind, typeLabel);
      const position = getAbsolutePosition(node, nodeMap);
      return {
        node,
        kind,
        filterKind: getFilterKind(kind),
        typeLabel,
        title,
        position,
        thumbnail: getNodeThumbnail(node, kind),
        status: getNodeStatus(node),
        Icon: getNodeIcon(node, kind),
      };
    }).sort((a, b) => (
      a.position.y - b.position.y
      || a.position.x - b.position.x
      || String(a.node.id).localeCompare(String(b.node.id))
    ));
  }, [nodes]);

  const filteredNodes = useMemo(() => {
    const keyword = query.trim().toLocaleLowerCase();
    return preparedNodes.filter((item) => {
      if (filter !== "all" && item.filterKind !== filter) return false;
      if (!keyword) return true;
      const data = item.node.data || {};
      const searchable = [
        item.title,
        item.typeLabel,
        item.node.id,
        data.prompt,
        data.description,
      ].map((value) => stringValue(String(value || ""))).join(" ").toLocaleLowerCase();
      return searchable.includes(keyword);
    });
  }, [filter, preparedNodes, query]);

  const handlePanelKeyDown = (event) => {
    // NodeCanvas 在 window 上监听空格、Delete、撤销等快捷键；面板内按键不能穿透到画布。
    event.stopPropagation();
    if (event.key === "Escape" && isOpen) {
      onClose?.();
    }
  };

  return (
    <aside
      ref={panelRef}
      role="dialog"
      aria-label="画布节点列表"
      aria-hidden={!isOpen}
      className="nodrag nopan nowheel absolute top-14 bottom-3 left-3 z-30 w-[300px] flex flex-col overflow-hidden rounded-xl border"
      style={{
        backgroundColor: "var(--color-bg-secondary)",
        borderColor: "var(--color-border-primary)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 20px 42px -12px rgba(0,0,0,0.62), 0 44px 84px -30px rgba(0,0,0,0.5)",
        opacity: isOpen ? 1 : 0,
        maxWidth: "calc(100% - 24px)",
        pointerEvents: isOpen ? "auto" : "none",
        transform: isOpen ? "translateX(0)" : "translateX(calc(-100% - 24px))",
        transition: "transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 160ms ease",
      }}
      onPointerDown={stopCanvasEvent}
      onMouseDown={stopCanvasEvent}
      onClick={stopCanvasEvent}
      onDoubleClick={stopCanvasEvent}
      onContextMenu={stopCanvasEvent}
      onWheel={stopCanvasEvent}
      onKeyDown={handlePanelKeyDown}
      onKeyUp={stopCanvasEvent}
    >
      <header
        className="h-12 shrink-0 flex items-center justify-between gap-3 border-b px-3.5"
        style={{ borderColor: "var(--color-border-secondary)" }}
      >
        <div className="min-w-0">
          <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
            节点列表
          </div>
          <div className="text-2xs" style={{ color: "var(--color-text-muted)" }} aria-live="polite">
            {filteredNodes.length === preparedNodes.length
              ? `共 ${preparedNodes.length} 个节点`
              : `${filteredNodes.length} / ${preparedNodes.length} 个节点`}
          </div>
        </div>
        <button
          type="button"
          className="nodrag nopan w-8 h-8 rounded-md flex items-center justify-center transition-colors hover:bg-[var(--color-bg-tertiary)]"
          style={{ color: "var(--color-text-secondary)" }}
          onClick={() => onClose?.()}
          tabIndex={isOpen ? 0 : -1}
          title="关闭节点列表"
          aria-label="关闭节点列表"
        >
          <X size={15} />
        </button>
      </header>

      <div className="shrink-0 space-y-2.5 border-b p-3" style={{ borderColor: "var(--color-border-secondary)" }}>
        <div
          className="nodrag nopan h-9 flex items-center gap-2 rounded-md border border-[var(--color-border-primary)] px-2.5 transition-colors focus-within:border-[var(--color-accent-primary)]"
          style={{ backgroundColor: "var(--color-bg-primary)" }}
        >
          <Search size={14} className="shrink-0" style={{ color: "var(--color-text-muted)" }} aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="nodrag nopan min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-[var(--color-text-muted)]"
            style={{ color: "var(--color-text-primary)" }}
            placeholder="搜索名称、提示词或节点 ID"
            tabIndex={isOpen ? 0 : -1}
            aria-label="搜索画布节点"
          />
          {query && (
            <button
              type="button"
              className="nodrag nopan w-5 h-5 shrink-0 rounded flex items-center justify-center transition-colors hover:bg-[var(--color-bg-tertiary)]"
              style={{ color: "var(--color-text-muted)" }}
              onClick={() => setQuery("")}
              tabIndex={isOpen ? 0 : -1}
              title="清空搜索"
              aria-label="清空节点搜索"
            >
              <X size={11} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1" role="group" aria-label="按节点类型筛选">
          {FILTERS.map((item) => {
            const active = filter === item.value;
            return (
              <button
                key={item.value}
                type="button"
                className="nodrag nopan min-w-0 flex-1 rounded-md px-1.5 py-1.5 text-2xs font-medium transition-colors"
                style={{
                  backgroundColor: active ? "var(--color-accent-light)" : "transparent",
                  color: active ? "var(--color-accent-primary)" : "var(--color-text-muted)",
                }}
                onClick={() => setFilter(item.value)}
                tabIndex={isOpen ? 0 : -1}
                aria-pressed={active}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto p-2">
        {filteredNodes.length > 0 ? (
          <div className="space-y-1" role="list">
            {filteredNodes.map((item) => {
              const { node, kind, typeLabel, title, thumbnail, status, Icon } = item;
              const typeMeta = TYPE_META[kind] || TYPE_META.other;
              const selected = selectedSet.has(String(node.id));
              return (
                <div key={node.id} role="listitem">
                  <button
                    type="button"
                    className="nodrag nopan group w-full flex items-center gap-2.5 rounded-lg border p-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-primary)]"
                    style={{
                      backgroundColor: selected ? "var(--color-accent-light)" : "transparent",
                      borderColor: selected ? "var(--color-accent-primary)" : "transparent",
                    }}
                    onClick={() => onLocateNode?.(node.id, node)}
                    onDoubleClick={() => onOpenNode?.(node.id, node)}
                    onKeyDown={(event) => {
                      if (event.key === " ") {
                        event.preventDefault();
                        onOpenNode?.(node.id, node);
                      }
                    }}
                    tabIndex={isOpen ? 0 : -1}
                    title="单击定位，双击打开；键盘空格打开"
                    aria-label={`${title}，${typeLabel}${status ? `，${status.label}` : ""}；单击定位，双击或空格打开`}
                    aria-current={selected ? "true" : undefined}
                  >
                    <span
                      className="relative w-11 h-11 shrink-0 overflow-hidden rounded-md border flex items-center justify-center"
                      style={{
                        backgroundColor: "var(--color-bg-tertiary)",
                        borderColor: "var(--color-border-secondary)",
                        color: typeMeta.color,
                      }}
                    >
                      <Icon size={17} aria-hidden="true" />
                      {thumbnail && (
                        <img
                          src={thumbnail}
                          alt=""
                          className="absolute inset-0 h-full w-full object-cover"
                          loading="lazy"
                          draggable={false}
                          onError={(event) => { event.currentTarget.style.display = "none"; }}
                        />
                      )}
                      {kind === "video" && thumbnail && (
                        <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <Video size={14} color="white" aria-hidden="true" />
                        </span>
                      )}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span
                        className="block truncate text-xs font-medium"
                        style={{ color: selected ? "var(--color-accent-primary)" : "var(--color-text-primary)" }}
                      >
                        {title}
                      </span>
                      <span className="mt-1 flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-2xs" style={{ color: "var(--color-text-muted)" }}>
                          {typeLabel}
                        </span>
                        {status && (
                          <span
                            className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-2xs font-medium"
                            style={{
                              backgroundColor: status.key === "failed"
                                ? "color-mix(in srgb, var(--color-danger) 12%, transparent)"
                                : "color-mix(in srgb, var(--color-accent-primary) 12%, transparent)",
                              color: status.key === "failed"
                                ? "var(--color-danger)"
                                : "var(--color-accent-primary)",
                            }}
                          >
                            {status.key === "failed"
                              ? <AlertCircle size={9} aria-hidden="true" />
                              : <Loader2 size={9} className="animate-spin" aria-hidden="true" />}
                            {status.label}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex h-full min-h-40 flex-col items-center justify-center px-5 text-center">
            <span
              className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}
            >
              {query || filter !== "all" ? <Search size={17} /> : <Layers size={17} />}
            </span>
            <div className="text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>
              {query || filter !== "all" ? "没有匹配的节点" : "画布上还没有节点"}
            </div>
            {(query || filter !== "all") && (
              <button
                type="button"
                className="nodrag nopan mt-2 text-2xs transition-colors hover:text-[var(--color-accent-primary)]"
                style={{ color: "var(--color-text-muted)" }}
                onClick={() => {
                  setQuery("");
                  setFilter("all");
                }}
                tabIndex={isOpen ? 0 : -1}
              >
                清除筛选
              </button>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
